// src/components/Navbar.js
// ──────────────────────────────────────────────────────────────────────────
// Two-tier institutional header (blue + lemon):
//   • Top utility strip  → contact info + quick links (Portal, Library, E-Learning)
//   • Main bar (sticky)   → wordmark + primary nav + lemon "Apply Now" button
// Data-driven: edit UTILITY_NAV / PRIMARY_NAV below. Item shapes:
//   { label, to }   internal route   | { label, href }  external (new tab)
//   { label, children:[...] }         dropdown
// NOTE: Portal/Library/E-Learning/Apply links are placeholders pointing to
// UNILUS as examples — swap them for Mbala Institute's own URLs.
// ──────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useSiteContent } from '../context/SiteContentContext';
import { useAuth } from '../context/AuthContext';
import theme from '../theme/theme';
import { SAMPLE_PROGRAMS } from '../api/programs';
import logo from '../assets/star.png';

const programmeChildren = [
  ...SAMPLE_PROGRAMS.slice(0, 8).map((p) => ({ label: p.title, to: `/programs/${p.id}` })),
  { label: 'All programmes →', to: '/programs' },
];

// ── Top utility strip (slim, right-aligned quick links) ───────────────────
const UTILITY_NAV = [
  { label: 'Portal', children: [
      { label: 'Students',   to: '/portal/student' },
      { label: 'Staff',      to: '/portal/staff' },
      { label: 'Applicants', to: '/apply' },
  ]},
  { label: 'Library',    to: '/library' },
];

// ── Main primary navigation ───────────────────────────────────────────────
const PRIMARY_NAV = [
  { label: 'Home', to: '/' },
  { label: 'Programmes', children: programmeChildren },
  { label: 'Fees & Tuition', children: [
      { label: 'Tuition fees', to: '/fees' },
      { label: 'Scholarships',       to: '/scholarships' },
  ]},
  { label: 'Accommodation', to: '/accommodation' },
  { label: 'About Us', children: [
      { label: 'Overview', to: '/about' },
      { label: 'News',     to: '/news' },
      { label: 'Contact',  to: '/contact' },
  ]},
];

// Apply Now CTA → our own application form at /apply

// ── Styled ────────────────────────────────────────────────────────────────
const Header = styled.header`
  position: relative;
  z-index: 200;
`;

const Utility = styled.div`
  background: ${theme.color.blueDeep};
  color: rgba(255,255,255,0.85);
  font-size: 0.78rem;
  @media (max-width: 980px) { display: none; }
`;

const UtilInner = styled.div`
  max-width: ${theme.layout.maxWidth};
  margin: 0 auto;
  padding: 0 ${theme.layout.gutter};
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const UtilContact = styled.div`
  display: flex; gap: 1.4rem;
  a { color: rgba(255,255,255,0.85); &:hover { color: ${theme.color.lemon}; } }
`;

const UtilLinks = styled.ul`
  display: flex; align-items: stretch; gap: 0.2rem;
  list-style: none; margin: 0; padding: 0; height: 38px;
`;

const UtilItem = styled.li`
  position: relative;
  display: flex; align-items: center;
  &:hover > div { opacity: 1; visibility: visible; transform: translateY(0); }
  > a, > span {
    display: inline-flex; align-items: center; gap: 4px;
    color: rgba(255,255,255,0.85);
    padding: 0 12px; height: 38px; cursor: pointer;
    &:hover { color: ${theme.color.lemon}; }
  }
  .caret { font-size: 0.55rem; opacity: 0.7; }
`;

const Main = styled.nav`
  position: sticky;
  top: 0;
  background: ${theme.color.paper};
  border-bottom: 1px solid ${theme.color.line};
  box-shadow: ${(p) => (p.$scrolled ? '0 3px 16px rgba(11,42,99,0.10)' : 'none')};
  transition: box-shadow 0.3s ${theme.ease};
  z-index: 200;
`;

const MainInner = styled.div`
  max-width: ${theme.layout.maxWidth};
  margin: 0 auto;
  padding: 0 ${theme.layout.gutter};
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
`;

const Wordmark = styled(Link)`
  display: flex; align-items: center; gap: 12px;
  img { height: 52px; width: 52px; object-fit: cover; border-radius: 50%; display: block; }
  .name { font-family: ${theme.font.display}; font-weight: 800; font-size: 1.05rem; letter-spacing: -0.01em; color: ${theme.color.ink}; line-height: 1; white-space: nowrap;
          @media (max-width: 600px) { display: none; } }
`;

const Menu = styled.ul`
  display: flex; align-items: center; gap: 0.2rem;
  list-style: none; margin: 0 0 0 auto; padding: 0;
  @media (max-width: 980px) { display: none; }
`;

const Item = styled.li`
  position: relative;
  &:hover > div { opacity: 1; visibility: visible; transform: translateY(0); }
`;

const Top = styled(NavLink)`
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.84rem; font-weight: 600;
  color: ${theme.color.ink};
  padding: 9px 13px; border-radius: 6px;
  white-space: nowrap; cursor: pointer;
  &.active, &:hover { color: ${theme.color.accent}; }
  .caret { font-size: 0.58rem; opacity: 0.6; }
`;

const Apply = styled(Link)`
  margin-left: 0.5rem;
  background: ${theme.color.lemon}; color: ${theme.color.onLemon};
  font-size: 0.84rem; font-weight: 700;
  padding: 10px 20px; border-radius: ${theme.layout.radius};
  white-space: nowrap;
  &:hover { filter: brightness(0.95); }
  @media (max-width: 980px) { display: none; }
`;

const Drop = styled.div`
  position: absolute;
  top: 100%; left: 0;
  min-width: 215px;
  background: ${theme.color.paper};
  border: 1px solid ${theme.color.line};
  border-top: 3px solid ${theme.color.lemon};
  border-radius: 0 0 ${theme.layout.radius} ${theme.layout.radius};
  box-shadow: 0 18px 40px -18px rgba(11,42,99,0.3);
  padding: 8px;
  opacity: 0; visibility: hidden;
  transform: translateY(8px);
  transition: all 0.2s ${theme.ease};
  z-index: 250;
  a {
    display: block; font-size: 0.9rem; font-weight: 500;
    color: ${theme.color.ink}; padding: 9px 12px; border-radius: 6px;
    white-space: nowrap;
    &:hover { background: ${theme.color.paperAlt}; color: ${theme.color.accent}; }
  }
`;
const DropRight = styled(Drop)` left: auto; right: 0; `;

const Burger = styled.button`
  display: none;
  @media (max-width: 980px) { display: flex; }
  flex-direction: column; gap: 5px;
  background: none; border: none; cursor: pointer; padding: 8px;
  margin-left: auto;
  span { width: 24px; height: 2px; background: ${theme.color.ink}; border-radius: 2px; }
`;

const Sheet = styled(motion.div)`
  position: fixed;
  inset: 70px 0 0 0;
  background: ${theme.color.paper};
  z-index: 199;
  overflow-y: auto;
  padding: 0.5rem ${theme.layout.gutter} 3rem;
`;

const MGroup = styled.div`
  border-bottom: 1px solid ${theme.color.line};
  > button, > a.single {
    width: 100%; text-align: left;
    display: flex; justify-content: space-between; align-items: center;
    background: none; border: none; cursor: pointer;
    font-family: ${theme.font.display};
    font-size: 1.05rem; font-weight: 700; color: ${theme.color.ink};
    padding: 15px 0;
  }
  .kids a { display: block; padding: 9px 0 9px 12px; color: ${theme.color.inkSoft}; font-size: 0.96rem; }
`;

const MApply = styled(Link)`
  display: block; text-align: center; margin-top: 1.4rem;
  background: ${theme.color.lemon}; color: ${theme.color.onLemon};
  font-weight: 700; padding: 14px; border-radius: ${theme.layout.radius};
`;

// ── Helper ────────────────────────────────────────────────────────────────
const ChildLink = ({ c, onClick }) =>
  c.href
    ? <a href={c.href} target="_blank" rel="noreferrer" onClick={onClick}>{c.label}</a>
    : <Link to={c.to} onClick={onClick}>{c.label}</Link>;

const Navbar = () => {
  const contact = useSiteContent('contact') || theme.info;
  const { isAdmin } = useAuth();
  const utilityNav = UTILITY_NAV.map((it) =>
    it.label === 'Portal'
      ? { ...it, children: isAdmin ? [...it.children, { label: 'Admin', to: '/admin' }] : it.children }
      : it
  );
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const ALL_MOBILE = [...PRIMARY_NAV, ...utilityNav];

  return (
    <Header>
      {/* Top utility strip */}
      <Utility>
        <UtilInner>
          <UtilContact>
            {contact?.phone && <a href={`tel:${contact.phone.replace(/\s/g, '')}`}>{contact.phone}</a>}
            {contact?.email && <a href={`mailto:${contact.email}`}>{contact.email}</a>}
          </UtilContact>
          <UtilLinks>
            {utilityNav.map((it) => (
              <UtilItem key={it.label}>
                {it.children ? (
                  <>
                    <span>{it.label} <span className="caret">▾</span></span>
                    <DropRight>{it.children.map((c) => <ChildLink key={c.label} c={c} />)}</DropRight>
                  </>
                ) : it.to ? (
                  <Link to={it.to}>{it.label}</Link>
                ) : (
                  <a href={it.href} target="_blank" rel="noreferrer">{it.label}</a>
                )}
              </UtilItem>
            ))}
          </UtilLinks>
        </UtilInner>
      </Utility>

      {/* Main bar */}
      <Main $scrolled={scrolled}>
        <MainInner>
          <Wordmark to="/">
            <img src={logo} alt="CGA Mbala Training Institute" />
          </Wordmark>

          <Menu>
            {PRIMARY_NAV.map((it) => (
              <Item key={it.label}>
                {it.children ? (
                  <>
                    <Top as="span">{it.label} <span className="caret">▾</span></Top>
                    <Drop>{it.children.map((c) => <ChildLink key={c.label} c={c} />)}</Drop>
                  </>
                ) : (
                  <Top to={it.to} end>{it.label}</Top>
                )}
              </Item>
            ))}
          </Menu>

          <Apply to="/apply">Apply Now</Apply>

          <Burger onClick={() => setOpen((v) => !v)} aria-label="Menu">
            <span /><span /><span />
          </Burger>
        </MainInner>
      </Main>

      {/* Mobile sheet */}
      <AnimatePresence>
        {open && (
          <Sheet
            initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 24 }} transition={{ duration: 0.25, ease: theme.ease }}
          >
            {ALL_MOBILE.map((it) => (
              it.children ? (
                <MGroup key={it.label}>
                  <button onClick={() => setExpanded(expanded === it.label ? null : it.label)}>
                    {it.label} <span>{expanded === it.label ? '–' : '+'}</span>
                  </button>
                  {expanded === it.label && (
                    <div className="kids">
                      {it.children.map((c) => <ChildLink key={c.label} c={c} onClick={() => setOpen(false)} />)}
                    </div>
                  )}
                </MGroup>
              ) : (
                <MGroup key={it.label}>
                  {it.to
                    ? <Link className="single" to={it.to} onClick={() => setOpen(false)}>{it.label}</Link>
                    : <a className="single" href={it.href} target="_blank" rel="noreferrer" onClick={() => setOpen(false)}>{it.label}</a>}
                </MGroup>
              )
            ))}
            <MApply to="/apply" onClick={() => setOpen(false)}>Apply Now →</MApply>
          </Sheet>
        )}
      </AnimatePresence>
    </Header>
  );
};

export default Navbar;
